import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { RegistrationRq, SourcePlatform } from 'graphql/generated';
import { RegisterForm } from '@app/core/types/authentication-form.type';
import { passwordMatchingValidator } from '@core/validators/password-matching.validator';
import { AuthService } from '@app/core/services/auth.service';
import { FormDateComponent } from '@app/core/components/forms/date/form-date.component';
import { FormInputComponent } from '@app/core/components/forms/input/form-input.component';
import { FormSelectComponent } from '@app/core/components/forms/select/form-select.component';
import { FormPasswordComponent } from '@app/core/components/forms/password/form-password.component';

@Component({
  standalone: true,
  selector: 'ups-register-modal',
  templateUrl: './auth-register-modal.component.html',
  styleUrls: [
    './../login/auth-login-modal.component.scss',
    './auth-register-modal.component.scss'
  ],
  imports: [
    IonicModule,
    TranslocoModule,
    ReactiveFormsModule,
    FormInputComponent,
    FormSelectComponent,
    FormDateComponent,
    FormPasswordComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterModalComponent implements OnInit, OnDestroy {

  @ViewChild('btn') protected btn!: ElementRef;
  public readonly genderOpt: { label: string, value: string }[] = [ { label: translate('form.male'), value: 'MALE'}, { label: translate('form.female'), value: 'FEMALE'} ];
  public readonly form: FormGroup<RegisterForm> = new FormGroup({
    phoneCode: new FormControl('62', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true, validators: Validators.required }),
    gender: new FormControl('', { nonNullable: true, validators: Validators.required }),
    dob: new FormControl('', { nonNullable: true, validators: Validators.required }),
    confirm: new FormControl('', { nonNullable: true, validators: Validators.required }),
    password: new FormControl('', { nonNullable: true, validators: Validators.required }),
    strategy: new FormControl<'email' | 'mobileNumber'>('email', { nonNullable: true }),
    mobileNumber: new FormControl<string | null>(null, [ Validators.pattern(/^[0-9]*$/) ]),
    email: new FormControl<string | null>(null, [ Validators.required, Validators.email ])
  }, { validators: passwordMatchingValidator });
  public isRegister: boolean = false;
  protected sub?: Subscription;

  constructor (
    protected readonly authService: AuthService,
    protected readonly alertCtrl: AlertController,
    protected readonly modalCtrl: ModalController,
  ) {}

  public ngOnInit(): void{ history.pushState({ regis: true }, ''); }

  public ngOnDestroy(): void{ 
    if (history.state.regis) history.back();
    this.sub?.unsubscribe();
  }

  @HostListener('window:popstate')
  public popState(): void{ this.modalCtrl.dismiss(); }

  public back(): void{
    if (this.isRegister === true) this.isRegister = false;
    else this.modalCtrl.dismiss();
  }

  public setStrategy(strategy: 'email' | 'mobileNumber'): void{
    this.isRegister = true;
    this.form.patchValue({ strategy });
  }

  public format(): void{
    let { strategy, mobileNumber } = this.form.value;
    if (strategy === 'mobileNumber') {
      mobileNumber = (mobileNumber?? '').replace(/\D/g, '').replace(/^0+/,'');
      
      if (mobileNumber.substring(0, 2) === '62') mobileNumber = mobileNumber.substring(2);
      this.form.patchValue({ mobileNumber });
    }
  }

  public register(): void{
    this.form.markAsTouched();
    if (!this.form.valid) return;
    
    this.toggleBtn();
    const { password, email, name, gender, dob, mobileNumber, phoneCode, strategy } = this.form.value;
    const data: RegistrationRq = {
      name: name!, 
      password: password!,
      gender: gender as any,
      sourcePlatform: SourcePlatform.CpCommerce,
      birthday: new Date(<string> dob).toISOString().split('T')[0]
    };

    if (strategy === 'email') data.email = email;
    else data.mobileNumber = `${ phoneCode }${ mobileNumber }`;

    this.sub?.unsubscribe();
    this.sub = this.authService.register(data).subscribe({
      next: ({ data: authMsRegister }) => {
        if (!authMsRegister) this.showAlert();
        else {
          this.showAlert(translate('message.verif_email_sent'), translate('title.email_sent'));
          this.modalCtrl.dismiss({ auto: true, cred: this.form.value });
        }
      },
      error: ({ message }) => {
        this.toggleBtn();
        this.showAlert(message);
      }
    });
  }

  protected toggleBtn(): void{
    this.btn?.nativeElement.toggleAttribute('disabled');
    this.btn?.nativeElement.classList.toggle('btn-loading');
  }

  protected async showAlert(message: string = translate('message.try_again'), header: string = translate('title.error')): Promise<void>{ (await this.alertCtrl.create({ header, message, buttons: ['OK'] })).present(); }
}
